import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { MRow } from '../../components/mobile/MobileKit';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { toast } from '../../stores/toast.store';
import { collegaPosta, PAROLE_POSTA, scollegaPosta, statoPostaQuery, useRitornoPosta } from '../assistente/postaApi';

/**
 * «La tua posta Google» nella scheda MUNE delle Impostazioni (Fase 2H, 29/10/2026): la casella di
 * chi è entrato, non dell'azienda. Collega manda il browser a Google; al ritorno l'avviso lo dà
 * useRitornoPosta. Scollega chiede conferma e toglie il permesso anche su Google.
 */
function usePostaGoogle() {
  useRitornoPosta();
  const qc = useQueryClient();
  const { data: p } = useQuery(statoPostaQuery);
  const collega = useMutation({
    mutationFn: collegaPosta,
    onError: (e: unknown) => toast.alert(PAROLE_POSTA.ritorno.errore, (e as Error)?.message),
  });
  const scollega = useMutation({
    mutationFn: scollegaPosta,
    onSuccess: () => {
      toast.ok(PAROLE_POSTA.scollegata);
      void qc.invalidateQueries({ queryKey: statoPostaQuery.queryKey });
    },
    onError: (e: unknown) => toast.alert('Posta non scollegata', (e as Error)?.message ?? 'Riprova'),
  });
  const chiediScollega = async () => {
    const ok = await confirmDialog({ title: 'Scollegare la posta?', body: PAROLE_POSTA.confermaScollega, confirmLabel: PAROLE_POSTA.scollega, danger: true });
    if (ok) scollega.mutate();
  };
  const spiega = !p
    ? ''
    : !p.pronta
      ? PAROLE_POSTA.spiegaSpenta
      : p.daRicollegare && p.email
        ? PAROLE_POSTA.spiegaDaRicollegare(p.email)
        : p.collegata && p.email
          ? PAROLE_POSTA.spiegaCollegata(p.email)
          : PAROLE_POSTA.spiegaNonCollegata;
  return { p, collega, scollega, chiediScollega, spiega };
}

/** Computer: una riga come gli interruttori sopra (adm-line), coi tasti a destra. */
export function PostaGoogleRiga() {
  const { p, collega, scollega, chiediScollega, spiega } = usePostaGoogle();
  if (!p) return null;
  const daCollegare = p.pronta && (!p.collegata || p.daRicollegare);
  return (
    <div className="adm-line">
      <div className="min-w-0 flex-1">
        <div className="text-ink font-medium">{PAROLE_POSTA.riga}</div>
        <div className="mute text-[12.5px]">{spiega}</div>
      </div>
      {p.pronta && (
        <div className="flex gap-2 shrink-0">
          {daCollegare && (
            <button type="button" className="btn btn-sm btn-primary" disabled={collega.isPending} onClick={() => collega.mutate()}>
              <Icon name="mail" size={10} /> {collega.isPending ? PAROLE_POSTA.collego : p.daRicollegare ? PAROLE_POSTA.ricollega : PAROLE_POSTA.collega}
            </button>
          )}
          {p.collegata && (
            <button type="button" className="btn btn-sm btn-ghost" disabled={scollega.isPending} onClick={() => void chiediScollega()}>
              <Icon name="unlink" size={10} /> {scollega.isPending ? PAROLE_POSTA.scollego : PAROLE_POSTA.scollega}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Telefono: una riga del gruppo MUNE; il tocco collega, o chiede se scollegare. */
export function PostaGoogleMobile() {
  const { p, collega, chiediScollega } = usePostaGoogle();
  if (!p) return null;
  if (!p.pronta) return <MRow icon="mail" title={PAROLE_POSTA.riga} sub={PAROLE_POSTA.spiegaSpenta} subWrap trail="non attiva" trailTone="mute" />;
  const daCollegare = !p.collegata || p.daRicollegare;
  return (
    <MRow
      icon="mail"
      tone={p.daRicollegare ? 'alert' : undefined}
      title={PAROLE_POSTA.riga}
      sub={p.email ?? 'Una mail parte solo quando premi Manda'}
      trail={collega.isPending ? PAROLE_POSTA.collego : p.daRicollegare ? 'da ricollegare' : p.collegata ? 'collegata' : 'collega'}
      trailTone={p.daRicollegare ? 'alert' : 'mute'}
      onClick={() => (daCollegare ? collega.mutate() : void chiediScollega())}
    />
  );
}
